import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {FieldError} from '../../../avisDePub/creer/services/field-error';
import {StepperBuilderService} from '../../../avisDePub/creer/services/stepper.builder.service';
import {ErreurMessages} from '../../models/component-messages/erreur-messages';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {first, filter, timeout} from 'rxjs/operators';
import {FormulaireServiceService} from '../../../formulaires/services/formulaire-service.service';
import {StepError} from '../../../avisDePub/creer/services/step-error';
import {StepWarning} from '../../../avisDePub/creer/services/step-warning';
import {ViewportScroller} from '@angular/common';
import {EformDuplicableEntityService} from "../../../formulaires/eforms/services/eform-duplicable-entity.service";
import {DuplicableElementIdPartEnum} from "../../../formulaires/eforms/enum/duplicable-element-id-part.enum";

@Component({
  selector: 'app-form-errors',
  templateUrl: './form.errors.component.html',
  styleUrls: ['./form.errors.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormErrorsComponent implements OnInit, OnChanges {

  // Types internes des éléments duplicables affichés en accordéon (cf. enumKeyToInternalType)
  private static readonly ACCORDION_TYPES = ['ORG', 'RES', 'TEN', 'TPA', 'CON'];
  // Sauvegarde de l'élément affiché + rechargement de l'avis + chargement de l'élément ciblé
  private static readonly ACCORDION_CHANGE_TIMEOUT_MS = 15000;

  @Input() stepid;
  @Input() linkForRouter: string;
  public erreurMessages = new ErreurMessages();

  warnings: BehaviorSubject<Array<FieldError>> = new BehaviorSubject([]);
  warningLength = 0;

  errors: BehaviorSubject<Array<FieldError>> = new BehaviorSubject([]);
  errorLength = 0;

  constructor(public stepBuilder: StepperBuilderService, public formulaireService: FormulaireServiceService,
              private cd: ChangeDetectorRef, private viewportScroller: ViewportScroller, private eformDuplicableEntityService: EformDuplicableEntityService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["stepid"]) {
      this.formulaireService.formulaireResponse.emit();
    }
  }

  scrollToFragment(fragment: string, event?: MouseEvent): void {
    event?.preventDefault();
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);

    // ✅ FIX 2026-02-05: Déterminer si un changement d'accordéon est nécessaire SANS déclencher le trigger
    const needsAccordionChange = this.checkIfAccordionChangeNeeded(fragment);

    const performScroll = () => {
      // Le fragment d'erreur contient l'index de l'élément duplicable (ex: Organization-Company_4 pour ORG-0005)
      // Mais dans le DOM, chaque accordéon réutilise le même template avec index 0
      // On doit donc remplacer l'index du champ duplicable par 0 (ex: Organization-Company_4 -> Organization-Company_0)
      let fragmentToScroll = fragment;

      // Remplacer l'index du pattern duplicable par 0
      Object.values(DuplicableElementIdPartEnum).forEach(pattern => {
        const regex = new RegExp(`(${this.escapeRegExp(pattern)})\\d+(_|$)`, 'g');
        fragmentToScroll = fragmentToScroll.replace(regex, `$10$2`);
      });

      const scrollToElement = (retryCount: number = 0) => {
        // PRB0054132 : quand le contrôle existait déjà à la création du champ (retour sur l'étape),
        // form-input suffixe l'id du champ par '_1' (voir idFromName) : on le cherche aussi sous ce nom.
        const element = document.getElementById(fragmentToScroll)
          || document.getElementById(fragmentToScroll + '_1');

        if (element) {
          // Double scroll pour garantir le bon positionnement :
          // 1. Premier scroll immédiat (sans animation) pour se rapprocher de l'élément
          // 2. Second scroll avec animation pour ajuster précisément la position
          element.scrollIntoView({ behavior: 'auto', block: 'center' });

          // Après le premier scroll, attendre que le layout se stabilise puis refaire un scroll smooth
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Mettre le focus sur l'élément après que le scroll soit terminé
            setTimeout(() => {
              this.focusElement(element);
            }, 300);
          }, 100);
        } else if (retryCount < 3) {
          // Réessayer après un délai si l'élément n'est pas encore dans le DOM
          setTimeout(() => scrollToElement(retryCount + 1), 200);
        } else {
          // Fallback: essayer avec viewportScroller si l'élément n'est pas trouvé après plusieurs essais
          this.viewportScroller.scrollToAnchor(fragmentToScroll);
        }
      };

      scrollToElement();
    };

    if (needsAccordionChange) {
      // ✅ FIX 2026-02-05: Mettre le flag à false AVANT de déclencher le trigger
      // pour ne pas rater le signal si le rechargement est très rapide
      this.eformDuplicableEntityService.isDataDuplicableEntityReady.next(false);

      // S'abonner AVANT de déclencher le trigger
      this.waitForDuplicableEntityDisplayed(duplicableInfo).subscribe({
        next: () => {
          // ✅ FIX 2026-02-09: Augmenter le délai pour le rendu DOM complet
          // 800ms permet à l'accordéon de s'ouvrir et aux champs de se rendre complètement
          setTimeout(() => performScroll(), 800);
        },
        error: () => {
          // En cas de timeout, essayer quand même de scroller
          console.warn('Timeout en attendant le chargement de l\'élément duplicable, tentative de scroll quand même');
          setTimeout(() => performScroll(), 800);
        }
      });

      // Maintenant déclencher le trigger APRÈS s'être abonné
      this.triggerDuplicableEntity(fragment);
    } else {
      // ✅ FIX 2026-02-09: Augmenter le délai pour les champs duplicables déjà visibles
      // 300ms permet de s'assurer que le DOM est stable
      setTimeout(() => performScroll(), 300);
    }
  }

  /**
   * PRB0053438 : attend que l'élément duplicable ciblé soit affiché et chargé avant de scroller.
   *
   * Pour un accordéon (organisation, résultat, offre...), ouvrir un autre élément passe d'abord par la sauvegarde
   * de l'élément affiché puis par le rechargement de l'avis (cf. DuplicableElementComponent.toggleAccordion) :
   * cela dépasse régulièrement 3 secondes, et le scroll partait alors avant l'ouverture de l'élément (il fallait
   * recliquer). On attend donc que l'accordéon ciblé soit celui affiché ET que ses données soient chargées,
   * avec un délai de sécurité plus conséquent.
   *
   * Les lots et parties sont des pages du stepper et non des accordéons : leur attente reste inchangée.
   */
  private waitForDuplicableEntityDisplayed(duplicableInfo: { key: string | null, index: number | null }): Observable<unknown> {
    const internalType = this.getInternalTypeFromEnumKey(duplicableInfo.key);
    const isReady$ = this.eformDuplicableEntityService.isDataDuplicableEntityReady;

    if (!FormErrorsComponent.ACCORDION_TYPES.includes(internalType)) {
      return isReady$.pipe(
        filter(isReady => isReady === true),
        first(),
        timeout(3000)
      );
    }

    return combineLatest([isReady$, this.eformDuplicableEntityService.expanded$]).pipe(
      filter(([isReady, expandedMap]) => isReady === true && expandedMap.get(internalType) === duplicableInfo.index),
      first(),
      timeout(FormErrorsComponent.ACCORDION_CHANGE_TIMEOUT_MS)
    );
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * ✅ FIX 2026-02-09: Met le focus sur l'élément cible ou son premier champ de saisie.
   * ✅ FIX 2026-03-11: Gère aussi le cas où l'élément lui-même est un champ focusable (input, select, textarea).
   * Cela garantit que l'utilisateur peut directement interagir avec le champ en erreur.
   */
  private focusElement(element: HTMLElement): void {
    // Vérifier d'abord si l'élément lui-même est un champ focusable (input, select, textarea)
    const tagName = element.tagName.toUpperCase();
    if (tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA') {
      setTimeout(() => {
        element.focus();
      }, 50);
      return;
    }

    // Sinon, chercher un champ de saisie enfant dans l'élément
    const focusableSelector = 'input:not([type="hidden"]), textarea, select, [contenteditable="true"]';
    const focusableChild = element.querySelector<HTMLElement>(focusableSelector);

    if (focusableChild) {
      // Si on trouve un champ de saisie enfant, le mettre en focus
      setTimeout(() => {
        focusableChild.focus();
      }, 50);
    } else if (element.tabIndex >= 0 || tagName === 'BUTTON' || tagName === 'A') {
      // Si l'élément lui-même est focusable (tabIndex ou bouton/lien)
      setTimeout(() => {
        element.focus();
      }, 50);
    }
  }

  /**
   * Mapping entre les clés de l'enum DuplicableElementIdPartEnum et les types internes (_idScheme)
   * utilisés par le service EformDuplicableEntityService.
   * Les clés de l'enum sont utilisées pour identifier les patterns dans les fragments d'URL,
   * mais le service utilise les types _idScheme pour gérer l'état des accordéons.
   */
  private readonly enumKeyToInternalType: { [key: string]: string } = {
    'GR-Organisations': 'ORG',
    'GR-Organisations_TEMP': 'ORG',
    'GR-LotResult': 'RES',
    'GR-LotTender': 'TEN',
    'GR-TenderingParty': 'TPA',
    'GR-SettledContract': 'CON',
    'GR-Lot': 'LOT'
  };

  /**
   * Convertit une clé d'enum en type interne pour le service
   */
  private getInternalTypeFromEnumKey(enumKey: string): string {
    return this.enumKeyToInternalType[enumKey] || enumKey;
  }

  /**
   * Extrait les informations de l'élément duplicable depuis le fragment
   * @param fragment Le fragment d'URL (ex: GR-Organisations-Section_0_0|BT-513-Organization-Company_1)
   * @returns Un objet avec la clé de l'élément duplicable et son index
   */
  private extractDuplicableInfoFromFragment(fragment: string): { key: string | null, index: number | null } {
    let key: string | null = null;
    let index: number | null = null;

    if (fragment.includes('|')) {
      const splittedName = fragment.split('|');
      const secondPart = splittedName[1];

      // Chercher quel pattern duplicable est présent dans le fragment
      // et extraire l'index qui suit ce pattern
      Object.entries(DuplicableElementIdPartEnum).forEach(([enumKey, patternValue]) => {
        if (secondPart && secondPart.includes(patternValue)) {
          key = enumKey.replace(/_TEMP$/, '');

          // ✅ FIX 2026-03-11: Extraire l'index en utilisant le pattern du duplicable
          // Le pattern contient un underscore à la fin (ex: "Organization-Company_")
          // L'index est le chiffre qui suit ce pattern
          const patternIndex = secondPart.indexOf(patternValue);
          if (patternIndex !== -1) {
            // Trouver le chiffre qui suit le pattern
            const afterPattern = secondPart.substring(patternIndex + patternValue.length);
            // Extraire les chiffres au début (avant tout autre caractère)
            const match = afterPattern.match(/^(\d+)/);
            if (match) {
              index = parseInt(match[1], 10);
              if (isNaN(index)) {
                index = null;
              }
            }
          }
        }
      });
    }

    return { key, index };
  }


  /**
   * Vérifie si un changement d'accordéon est nécessaire SANS déclencher le trigger.
   * Cette méthode est utilisée pour déterminer si on doit attendre le rechargement de l'accordéon.
   */
  private checkIfAccordionChangeNeeded(fragment: string): boolean {
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);
    const key = duplicableInfo.key;
    const index = duplicableInfo.index;

    if (key && index !== null) {
      const internalType = this.getInternalTypeFromEnumKey(key);
      const currentDisplayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(internalType);
      return currentDisplayedIndex !== index;
    }
    return false;
  }

  /**
   * Déclenche le trigger pour ouvrir l'accordéon correspondant au fragment.
   * Cette méthode est appelée APRÈS s'être abonné au signal isDataDuplicableEntityReady.
   */
  private triggerDuplicableEntity(fragment: string): void {
    const duplicableInfo = this.extractDuplicableInfoFromFragment(fragment);
    const key = duplicableInfo.key;
    const index = duplicableInfo.index;

    if (key && index !== null) {
      this.eformDuplicableEntityService.triggerDuplicableEntity(key, index);
    }
  }

  public ngOnInit() {

    this.formulaireService.formulaireResponse.subscribe(
      () => {
        let warnings = this.getWarnings();
        this.warnings.next(warnings);
        this.warningLength = warnings.length;

        let errors = this.getErrors();
        this.errors.next(errors);
        this.errorLength = errors.length;
      }
    );

    this.stepBuilder.newError.subscribe((val) => {
      if (val) {
        let errors = this.getErrors();
        this.errors.next(errors);
        this.errorLength = errors.length;
        this.stepBuilder.newError.next(false);
      }
    })

    this.stepBuilder.newWarning.subscribe((val) => {
      if (val) {
        let warnings = this.getWarnings();
        this.warnings.next(warnings);
        this.warningLength = warnings.length;
        this.stepBuilder.newWarning.next(false);
      }
    })

  }

  public getErrors(): Array<FieldError> {
    let errors: Array<FieldError>;

    // Cas spéciaux avec SepSep (TypeFacturation, TypeIndexation, idAnnonceur)
    if (this.isSpecialSepSepCase()) {
      errors = this.handleSpecialSepSepCase();
    }
    // Cas avis nationaux avec pattern _STEP_ID_X_SepSep_... (ex: _STEP_ID_1_SepSep_Annonce_...)
    else if (this.isNationalNoticeCase()) {
      errors = this.handleNationalNoticeCase();
    }
    // Cas avec pattern _X_Y (ex: GR-Buyer_0_1, GR-Result_0_3)
    else if (this.hasMultipleNumericSegments() && !this.isSepSepSubSection()) {
      errors = this.handleMultipleNumericSegments();
    }
    // Cas simple avec un seul chiffre à la fin (ex: _STEP_ID_1)
    else if (this.hasSimpleNumericSuffix() && !this.isSepSepSubSection()) {
      errors = this.handleSimpleNumericSuffix();
    }
    // Cas normal : retourne uniquement les erreurs de la sous-section exacte
    else {
      errors = this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    }

    // Tri des erreurs par index
    return this.sortErrorsByIndex(errors);
  }

  /**
   * Vérifie si le stepid est un cas spécial avec SepSep
   */
  private isSpecialSepSepCase(): boolean {
    return this.stepid == '_STEP_ID_SepSep_TypeFacturation' ||
      this.stepid == '_STEP_ID_SepSep_TypeIndexation' ||
      this.stepid == '_STEP_ID_SepSep_idAnnonceur';
  }

  /**
   * Gère les cas spéciaux avec SepSep (Facturation, Indexation, Annonceur)
   */
  private handleSpecialSepSepCase(): Array<FieldError> {
    if (this.stepid == '_STEP_ID_SepSep_TypeFacturation') {
      let facturationErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_TypeFacturation');
      this.stepBuilder.facturationErrorCount = facturationErrorList.length;
      return facturationErrorList;
    } else if (this.stepid == '_STEP_ID_SepSep_TypeIndexation') {
      let indexationErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_TypeIndexation');
      this.stepBuilder.indexationErrorCount = indexationErrorList.length;
      return indexationErrorList;
    } else if (this.stepid == '_STEP_ID_SepSep_idAnnonceur') {
      let idAnnonceurErrorList = this.extractValuesByKey(this.stepBuilder.stepFieldsOnErrors, '_SepSep_idAnnonceur');
      this.stepBuilder.annonceurErrorCount = idAnnonceurErrorList.length;
      return idAnnonceurErrorList;
    }
    return [];
  }

  /**
   * Vérifie si le stepid contient SepSep
   */
  private isSepSepSubSection(): boolean {
    return this.stepid.includes('SepSep');
  }

  /**
   * Vérifie si c'est un cas d'avis national avec pattern _STEP_ID_X_SepSep_...
   * Ex: "_STEP_ID_1_SepSep_Annonce_..." ou "lieuExecutionLivraison_STEP_ID_2_SepSep_..."
   */
  private isNationalNoticeCase(): boolean {
    return /_STEP_ID_\d+_SepSep_/.test(this.stepid);
  }

  /**
   * Gère les cas d'avis nationaux
   * Retourne uniquement les erreurs de cette étape spécifique
   */
  private handleNationalNoticeCase(): Array<FieldError> {
    // Pour les avis nationaux, chaque stepid est unique et correspond à une sous-section spécifique
    // On retourne donc uniquement les erreurs de ce stepid exact
    return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
  }

  /**
   * Vérifie si le stepid a plusieurs segments numériques (ex: _0_1, _1_0)
   */
  private hasMultipleNumericSegments(): boolean {
    return /_\d+_\d+$/.test(this.stepid);
  }

  /**
   * Vérifie si le stepid a un simple suffixe numérique (ex: _1, _2)
   */
  private hasSimpleNumericSuffix(): boolean {
    return /_\d+$/.test(this.stepid);
  }

  /**
   * Gère les cas avec pattern _X_Y (ex: GR-Buyer_0_1 ou GR-Result_0_3)
   * Différencie les groupes duplicables des sous-sections distinctes
   */
  private handleMultipleNumericSegments(): Array<FieldError> {
    const parts = this.stepid.split('_');
    const secondLastNumber = parts[parts.length - 2];

    // Vérifie si le groupe a des sous-sections dans le stepper
    if (this.groupHasSections()) {
      // Sous-sections distinctes (ex: GR-Result) -> erreurs séparées
      return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    } else {
      // Groupe duplicable (ex: GR-Buyer) -> agrège les instances
      return this.aggregateErrorsForDuplicableGroup(parts, secondLastNumber);
    }
  }

  /**
   * Vérifie si le groupe a des sous-sections dans le stepper
   */
  private groupHasSections(): boolean {
    const groupName = this.extractGroupName();

    if (!groupName || !this.stepBuilder.stepper) {
      return false;
    }

    const stepperValue = this.stepBuilder.stepper.value;
    if (!stepperValue || !Array.isArray(stepperValue) || stepperValue.length === 0) {
      return false;
    }

    const step = stepperValue.find(s => s && s.eformName === groupName);
    return !!(step && step.sections && Array.isArray(step.sections) && step.sections.length > 0);
  }

  /**
   * Extrait le nom du groupe depuis le stepid (ex: "GR-Buyer", "GR-Result")
   */
  private extractGroupName(): string | null {
    // Gère deux cas : avec ou sans le préfixe _STEP_ID_
    // Ex: "_STEP_ID_GR-Buyer_0_0" ou "GR-Buyer_0_0"
    let groupNameMatch = this.stepid.match(/^_STEP_ID_([A-Za-z\-]+)_/);
    if (groupNameMatch) {
      return groupNameMatch[1];
    }

    // Si pas de préfixe _STEP_ID_, extrait directement le nom avant le premier underscore suivi de chiffres
    groupNameMatch = this.stepid.match(/^([A-Za-z\-]+)_\d+_\d+$/);
    return groupNameMatch ? groupNameMatch[1] : null;
  }

  /**
   * Agrège les erreurs pour un groupe duplicable
   */
  private aggregateErrorsForDuplicableGroup(parts: string[], secondLastNumber: string): Array<FieldError> {
    const stepBaseId = this.stepid.substring(0, this.stepid.lastIndexOf('_') + 1);
    const keys = Array.from(this.stepBuilder.stepFieldsOnErrors.keys());

    const matchingKeys = keys.filter(key => {
      if (!key.startsWith(stepBaseId)) return false;
      const keyParts = key.split('_');
      return keyParts.length === parts.length &&
        keyParts[keyParts.length - 2] === secondLastNumber;
    });

    if (matchingKeys.length > 1) {
      return this.collectErrorsFromKeys(matchingKeys);
    } else {
      return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
    }
  }

  /**
   * Gère les cas simples avec un seul chiffre à la fin (ex: _STEP_ID_1)
   * Pour les avis nationaux, chaque _STEP_ID_X correspond à une page/étape distincte
   * On ne doit donc PAS agréger les erreurs
   */
  private handleSimpleNumericSuffix(): Array<FieldError> {
    // Pour les avis nationaux (_STEP_ID_1, _STEP_ID_2, etc.), chaque stepid est une page distincte
    // On retourne uniquement les erreurs de ce stepid exact
    return this.stepBuilder.getStepFieldsOnErrors(this.stepid);
  }

  /**
   * Collecte les erreurs depuis une liste de clés
   */
  private collectErrorsFromKeys(keys: string[]): Array<FieldError> {
    let allErrors: any[] = [];
    for (const key of keys) {
      const errors = this.stepBuilder.getStepFieldsOnErrors(key);
      if (errors.length > 0) {
        allErrors = allErrors.concat(errors);
      }
    }
    return allErrors;
  }

  /**
   * Trie les erreurs par ordre d'index numérique
   * Extrait les index depuis les fragments et trie les erreurs en conséquence
   */
  private sortErrorsByIndex(errors: Array<FieldError>): Array<FieldError> {
    if (!errors || errors.length === 0) {
      return errors;
    }

    return errors.sort((a, b) => {
      const indexA = this.extractIndexFromFragment(a.fieldName);
      const indexB = this.extractIndexFromFragment(b.fieldName);

      // Si les deux ont des index, comparer numériquement
      if (indexA !== null && indexB !== null) {
        return indexA - indexB;
      }

      // Si seulement A a un index, le mettre après
      if (indexA !== null && indexB === null) {
        return 1;
      }

      // Si seulement B a un index, le mettre après
      if (indexA === null && indexB !== null) {
        return -1;
      }

      // Si aucun n'a d'index, trier alphabétiquement par nom
      return (a.fieldName || '').localeCompare(b.fieldName || '');
    });
  }

  /**
   * Extrait l'index numérique depuis un fragment/nom de champ
   * Supporte différents formats: Organization_2, GR-Buyer_0_1, _STEP_ID_3, etc.
   */
  private extractIndexFromFragment(fieldName: string): number | null {
    if (!fieldName) {
      return null;
    }

    // Gère les cas avec pipe (|) pour les entités duplicables
    if (fieldName.includes('|')) {
      const parts = fieldName.split('|');
      if (parts[1] && parts[1].includes('_')) {
        const indexMatch = parts[1].match(/_(\d+)$/);
        if (indexMatch) {
          return parseInt(indexMatch[1], 10);
        }
      }
    }

    // Extrait le dernier nombre du nom de champ
    const matches = fieldName.match(/_(\d+)(?:_\d+)?$/);
    if (matches && matches[1]) {
      return parseInt(matches[1], 10);
    }

    return null;
  }

  public getWarnings(): Array<FieldError> {
    let warnings: Array<FieldError>;

    if (this.stepid == '_STEP_ID_SepSep_TypeFacturation') {
      let facturationWarningList = this.extractWarningValuesByKey(this.stepBuilder.stepFieldsOnWarnings, '_SepSep_TypeFacturation');
      this.stepBuilder.facturationWarningCount = facturationWarningList.length;
      warnings = facturationWarningList;
    } else if (this.stepBuilder.getStepFieldsOnWarnings(this.stepid).length > 0) {
      warnings = this.stepBuilder.getStepFieldsOnWarnings(this.stepid);
    } else {
      warnings = [];
    }

    // Tri des warnings par index également
    return this.sortErrorsByIndex(warnings);
  }

  extractValuesByKey(map: Map<string, StepError>, value: string): any[] {
    const result: Array<FieldError> = [];
    for (const [key, element] of map.entries()) {
      if (key?.includes(value)) {
        for (let val of element.errors) {
          result.push(val);
        }
      }
    }
    return result;
  }

  extractWarningValuesByKey(map: Map<string, StepWarning>, value: string): any[] {
    const result: Array<FieldError> = [];
    for (const [key, element] of map.entries()) {
      if (key?.includes(value)) {
        for (let val of element.warnings) {
          result.push(val);
        }
      }
    }
    return result;
  }
}
